import { useState, useMemo } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Users, MapPin, UserPlus, Loader2, Check } from "lucide-react";
import { useAllPlayers, RealPlayer } from '@/hooks/useAllPlayers';
import { useFriends } from '@/hooks/useFriends';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";

const ExplorePlayers = () => {
const [searchQuery, setSearchQuery] = useState("");
  const [loadingRequests, setLoadingRequests] = useState<Set<string>>(new Set());
  
  const isMobile = useIsMobile();
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const { players, loading, error, searchPlayers } = useAllPlayers();
  const { 
    followUser, 
    getRelationshipStatus, 
    loading: friendsLoading 
  } = useFriends();

  const handleViewProfile = (playerId: string) => {
    navigate(`/profile/${playerId}`, { 
      state: { from: '/explore-players' } 
    });
  };

  // Filter players based on search (current user already excluded in hook)
  const filteredPlayers = useMemo(() => {
    if (searchQuery.trim().length < 2) return [];
    return searchPlayers(searchQuery);
  }, [players, searchQuery, searchPlayers]);

  const handleFollowPlayer = async (player: RealPlayer) => {
    setLoadingRequests(prev => new Set(prev).add(player.id));
    try {
      const result = await followUser(player.id);
      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success(`Ahora sigues a ${player.full_name}`);
      }
    } catch (error) {
      console.error("Error following user:", error);
      toast.error("Error al seguir usuario");
    } finally {
      setLoadingRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(player.id);
        return newSet;
      });
    }
  };

  const handleFollowBack = async (player: RealPlayer) => {
    setLoadingRequests(prev => new Set(prev).add(player.id));
    try {
      const result = await followUser(player.id);
      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success(`Ahora sigues a ${player.full_name}`);
      }
    } catch (error) {
      console.error("Error following user:", error);
      toast.error("Error al seguir usuario");
    } finally {
      setLoadingRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(player.id);
        return newSet;
      });
    }
  };

  const getButtonContent = (player: RealPlayer) => {
    const relationshipStatus = getRelationshipStatus(player.id);
    const isLoading = loadingRequests.has(player.id);

    if (isLoading) {
      return {
        icon: Loader2,
        text: "Enviando...",
        variant: "outline" as const,
        disabled: true,
        iconClass: "animate-spin"
      };
    }

    switch (relationshipStatus) {
      case 'mutual_following':
        return {
          icon: Check,
          text: "Amigos",
          variant: "default" as const,
          disabled: true,
          iconClass: ""
        };
      case 'i_follow_them':
        return {
          icon: Check,
          text: "Lo sigues",
          variant: "outline" as const,
          disabled: true,
          iconClass: ""
        };
      case 'they_follow_me':
        return {
          icon: UserPlus,
          text: "Seguir de vuelta",
          variant: "default" as const,
          disabled: false,
          iconClass: "",
          onClick: () => handleFollowBack(player)
        };
      default:
        return {
          icon: UserPlus,
          text: "Seguir",
          variant: "outline" as const,
          disabled: false,
          iconClass: "",
          onClick: () => handleFollowPlayer(player)
        };
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card className="bg-white">
          <CardContent className={`${isMobile ? 'p-6' : 'p-12'} text-center`}>
            <Loader2 className={`${isMobile ? 'h-6 w-6' : 'h-8 w-8'} text-gray-400 mx-auto mb-4 animate-spin`} />
            <p className="text-gray-600">Cargando jugadores...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Card className="bg-white">
          <CardContent className={`${isMobile ? 'p-6' : 'p-12'} text-center`}>
            <Users className={`${isMobile ? 'h-8 w-8' : 'h-12 w-12'} text-gray-400 mx-auto mb-4`} />
            <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium text-gray-900 mb-2`}>Error al cargar jugadores</h3>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <Card className="bg-white">
        <CardContent className={`${isMobile ? 'p-3' : 'p-4'}`}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Buscar por nombre, email o ubicación..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 placeholder:text-xs placeholder:text-gray-400"
            />
          </div>
        </CardContent>
      </Card>

      {/* Players List */}
      <div className={`grid ${isMobile ? 'gap-3 max-w-full' : 'gap-4'}`}>
        {filteredPlayers.length > 0 ? (
          filteredPlayers.map((player) => (
            <Card 
              key={player.id} 
              className="hover:shadow-lg transition-shadow duration-200 bg-white border-gray-200 cursor-pointer overflow-hidden"
              onClick={() => handleViewProfile(player.id)}
            >
              <CardContent className={`${isMobile ? 'p-3 overflow-hidden' : 'p-6'}`}>
                <div className={`${isMobile ? 'flex items-center gap-2 w-full overflow-hidden' : 'flex flex-col gap-4'}`}>
                  {isMobile ? (
                    <>
                      {/* Avatar */}
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm font-bold shrink-0">
                        {getInitials(player.full_name)}
                      </div>
                      {/* Info - truncated */}
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <h3 className="text-sm font-bold text-foreground truncate">{player.full_name}</h3>
                        <div className="flex items-center text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3 mr-1 shrink-0" />
                          <span className="truncate">{player.location}</span>
                        </div>
                        <div className="flex items-center text-xs text-muted-foreground">
                          <Users className="h-3 w-3 mr-1 shrink-0" />
                          <span className="truncate">ID: {player.id.slice(0, 8)}</span>
                        </div>
                      </div>
                      {/* Buttons - vertical stack */}
                      <div className="flex flex-col gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const buttonContent = getButtonContent(player);
                          const IconComponent = buttonContent.icon;
                          return (
                            <Button
                              variant={buttonContent.variant}
                              size="xs"
                              onClick={buttonContent.onClick}
                              disabled={buttonContent.disabled}
                              className="flex items-center gap-1 text-xs px-3 h-7 w-full whitespace-nowrap"
                            >
                              <IconComponent className={`h-3 w-3 ${buttonContent.iconClass}`} />
                              {buttonContent.text}
                            </Button>
                          );
                        })()}
                        <Button 
                          size="xs"
                          className="bg-blue-600 hover:bg-blue-700 flex items-center gap-1 text-xs px-3 h-7 w-full whitespace-nowrap"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewProfile(player.id);
                          }}
                        >
                          Ver perfil
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-lg font-bold shrink-0">
                        {getInitials(player.full_name)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="text-lg font-bold text-foreground">{player.full_name}</h3>
                            <div className="flex items-center text-sm text-muted-foreground">
                              <MapPin className="h-4 w-4 mr-1" />
                              <span>{player.location}</span>
                            </div>
                            <div className="flex items-center text-sm text-muted-foreground mt-1">
                              <Users className="h-4 w-4 mr-1" />
                              <span>ID: {player.id.slice(0, 8)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex-1" />
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          {(() => {
                            const buttonContent = getButtonContent(player);
                            const IconComponent = buttonContent.icon;
                            return (
                              <Button
                                variant={buttonContent.variant}
                                size="sm"
                                onClick={buttonContent.onClick}
                                disabled={buttonContent.disabled}
                                className="flex items-center gap-2"
                              >
                                <IconComponent className={`h-4 w-4 ${buttonContent.iconClass}`} />
                                {buttonContent.text}
                              </Button>
                            );
                          })()}
                          <Button 
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewProfile(player.id);
                            }}
                          >
                            Ver perfil
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        ) : searchQuery.trim().length < 2 ? (
          <Card className="bg-white">
            <CardContent className={`${isMobile ? 'p-6' : 'p-12'} text-center`}>
              <Search className={`${isMobile ? 'h-8 w-8' : 'h-12 w-12'} text-muted-foreground mx-auto mb-4`} />
              <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium text-foreground mb-2`}>Busca jugadores</h3>
              <p className="text-muted-foreground">Escribe al menos 2 caracteres para buscar por nombre o ubicación</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white">
            <CardContent className={`${isMobile ? 'p-6' : 'p-12'} text-center`}>
              <Users className={`${isMobile ? 'h-8 w-8' : 'h-12 w-12'} text-muted-foreground mx-auto mb-4`} />
              <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium text-foreground mb-2`}>No se encontraron jugadores</h3>
              <p className="text-muted-foreground">Intenta ajustar tu búsqueda</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ExplorePlayers;
